// src/pages/EditArticle.js
import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig"; // CSRF設定
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API_URL from "../config";
import MarkdownIt from "markdown-it"; // Markdown 解析器 (Markdown Parser)
import MdEditor from "react-markdown-editor-lite"; // Markdown 編輯器 (Markdown Editor)
import "react-markdown-editor-lite/lib/index.css"; // Markdown 編輯器樣式
import { toast } from 'react-toastify';


const mdParser = new MarkdownIt();

function EditArticle() {
  const { id } = useParams(); // 從路徑中獲取文章 ID
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 管理文章欄位的 state（中文內容）
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");

  // 刪除原有的翻譯 state
  // 新增：英文版各欄位 state
  const [engTitle, setEngTitle] = useState("");
  const [engSummary, setEngSummary] = useState("");
  const [engContent, setEngContent] = useState("");
  const [engTags, setEngTags] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);

  // 從 Markdown 內容中提取圖片 URL 的函式
  const extractImageUrls = (content) => {
    // 正則：匹配 Markdown 圖片語法 ![alt](url)
    const regex = /!\[[^\]]*\]\((.*?)\)/g;
    let urls = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      urls.push(match[1]);
    }
    return urls;
  };

  // 當 content 或 engContent 改變時，檢查上傳的圖片中哪些已不在 Markdown 中，進而刪除
  useEffect(() => {
    // 修改：同時檢查中文和英文內容中的圖片
    const chineseImageUrls = extractImageUrls(content);
    const englishImageUrls = extractImageUrls(engContent);
    const currentImageUrls = [...chineseImageUrls, ...englishImageUrls];
    // 找出 uploadedImages 中不再出現的圖片
    const imagesToDelete = uploadedImages.filter((url) => !currentImageUrls.includes(url));
    
    imagesToDelete.forEach(async (url) => {
      try {
        await axios.delete(`${API_URL}/api/delete_image`, {
          data: { imageUrl: url },
          withCredentials: true,
        });
        console.log(`已刪除圖片: ${url}`);
        // 更新 state，移除已刪除的圖片
        setUploadedImages((prev) => prev.filter((item) => item !== url));
      } catch (error) {
        console.error(`刪除圖片失敗: ${url}`, error);
      }
    });
  }, [content, engContent, uploadedImages]); // 修改：加入 engContent 為依賴

  // 從 API 獲取文章資料，並初始化各欄位
  useEffect(() => {
    async function fetchArticle() {
      try {
        const response = await axios.get(`${API_URL}/api/articles/${id}`);
        const article = response.data;
        setTitle(article.title);
        setSummary(article.summary);
        setContent(article.content);
        setTags(article.tags.join(", "));
        
        // 新增：從文章翻譯中初始化英文欄位
        if (article.translations && article.translations.en) {
          setEngTitle(article.translations.en.title || "");
          setEngSummary(article.translations.en.summary || "");
          setEngContent(article.translations.en.content || "");
          setEngTags(article.translations.en.tags ? article.translations.en.tags.join(", ") : "");
        }
        
        // 修改：同時從中文與英文內容提取圖片 URL
        const chineseImages = extractImageUrls(article.content);
        const englishImages = (article.translations && article.translations.en && article.translations.en.content)
          ? extractImageUrls(article.translations.en.content)
          : [];
        setUploadedImages([...chineseImages, ...englishImages]);
      } catch (err) {
        console.error("獲取文章資料時發生錯誤：", err);
        setError(t("errorFetchingArticle"));
      }
    }
    fetchArticle();
  }, [id, t]);

  // 處理圖片上傳至 AWS S3
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API_URL}/api/upload_images`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true, // 確保 CSRF 保護
      });

      const imageUrl = response.data.url; // 獲取 S3 回傳的圖片 URL

      // 將上傳成功的圖片 URL 存入 state
      setUploadedImages((prevImages) => [...prevImages, imageUrl]);

      return imageUrl;
    } catch (error) {
      console.error("圖片上傳失敗:", error);
      return "";
    }
  };

  // 刪除所有上傳但未使用的圖片
  const deleteUploadedImages = async () => {
    for (const imageUrl of uploadedImages) {
      try {
        await axios.delete(`${API_URL}/api/delete_image`, {
          data: { imageUrl: imageUrl },
          withCredentials: true,
        });
        console.log(`已刪除圖片: ${imageUrl}`);
      } catch (error) {
        console.error(`刪除圖片失敗: ${imageUrl}`, error);
      }
    }
    // 刪除完後，清空 uploadedImages state
    setUploadedImages([]);
  };

  // 提交表單，更新文章資料
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const tagsArray = tags.split(",").map((tag) => tag.trim());
      const engTagsArray = engTags.split(",").map((tag) => tag.trim()); // 新增：處理英文標籤
      await axios.put(
        `${API_URL}/api/admin/articles/${id}`,
        {
          title,
          summary,
          content,
          tags: tagsArray,
          translations: {
            en: {
              title: engTitle,       // 新增：英文標題
              summary: engSummary,   // 新增：英文摘要
              content: engContent,   // 新增：英文內容
              tags: engTagsArray     // 新增：英文標籤
            }
          }
        },
        { withCredentials: true }
      );
      setSuccess(t("articleUpdatedSuccess")); // 成功提示訊息
      setTimeout(() => navigate("/admin/articles"), 1500); // 延遲導航回文章列表
    } catch (err) {
      console.error("更新文章時發生錯誤：", err);
      setError(t("errorUpdatingArticle"));
    }
  };

  // 取消編輯：先刪除上傳圖片，再返回管理頁面
  const handleCancel = async () => {
    await deleteUploadedImages();
    navigate("/admin/articles");
  };

  const handleDelete = async () => {
    // 可加上確認提示，避免誤觸
    if (window.confirm("確定要刪除這篇文章嗎？")) {
      try {
        await axios.delete(`${API_URL}/api/admin/articles/${id}`, { withCredentials: true });
        // 如使用 react-toastify，可顯示成功提示，否則可改用 alert
        toast.success(t("articleDeletedSuccessfully"));
        // 刪除後導向文章列表頁面
        navigate("/admin/articles");
      } catch (err) {
        console.error("刪除文章時發生錯誤：", err);
        toast.error(t("errorDeletingArticle"));
      }
    }
  };



  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-black dark:text-white">
      <div className="container mx-auto px-4 py-6">
        <h2 className="text-2xl font-bold mb-4">{t("editArticle")}</h2>

        {error && <p className="text-red-500 mb-4">{error}</p>}
        {success && <p className="text-green-500 mb-4">{success}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 中文版文章欄位 */}
          <div>
            <label className="block mb-1">{t("title")}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block mb-1">{t("summary")}</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block mb-1">{t("content")}</label>
            <MdEditor
              value={content}
              style={{ height: "500px" }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setContent(text)}
              onImageUpload={handleImageUpload}
            />
          </div>
          <div>
            <label className="block mb-1">{t("tags")}</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t("tagsPlaceholder")}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>

          <hr className="my-6" />
          <h3 className="text-xl font-bold mb-4">英文版文章</h3>
          {/* 新增：英文版文章欄位 */}
          <div>
            <label className="block mb-1">英文標題</label>
            <input
              type="text"
              value={engTitle}
              onChange={(e) => setEngTitle(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block mb-1">英文摘要</label>
            <textarea
              value={engSummary}
              onChange={(e) => setEngSummary(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block mb-1">英文內容</label>
            <MdEditor
              value={engContent}
              style={{ height: "500px" }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setEngContent(text)}
              onImageUpload={handleImageUpload}
            />
          </div>
          <div>
            <label className="block mb-1">英文標籤</label>
            <input
              type="text"
              value={engTags}
              onChange={(e) => setEngTags(e.target.value)}
              placeholder="請以逗號分隔標籤"
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>

          <div className="flex items-center justify-between">
            {/* 左側：取消與提交 */}
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                {t("submit")}
              </button>
            </div>
            {/* 右側：刪除文章 */}
            <div>
              <button
                type="button"
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-800 text-white font-bold py-2 px-4 rounded"
              >
                {t("deleteArticle")}
              </button>
            </div>
          </div>

        </form>
      </div>
    </div>
  );
}

export default EditArticle;
